import * as React from 'react';
import { useEffect } from 'react';

import { Stack, Tooltip } from '@mui/material';
import Box, { BoxProps } from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Typography from '@mui/material/Typography';

interface GridCellExpandProps extends BoxProps {
  value: string;
  width: number;
  cellStyle?: any;
  icon?: React.ReactElement;
}

function isOverflown(element: Element): boolean {
  return (
    element.scrollHeight > element.clientHeight ||
    element.scrollWidth > element.clientWidth
  );
}

export const GridCellExpand = React.memo(function GridCellExpand(
  props: GridCellExpandProps
) {
  const { width, value, cellStyle, icon, ...rest } = props;
  const wrapper = React.useRef<HTMLDivElement | null>(null);
  const cellDiv = React.useRef(null);
  const cellValue = React.useRef(null);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [showFullCell, setShowFullCell] = React.useState(false);
  const [showPopper, setShowPopper] = React.useState(false);

  const handleMouseEnter = () => {
    const isCurrentlyOverflown = isOverflown(cellValue.current!);
    setShowPopper(isCurrentlyOverflown);
    setAnchorEl(cellDiv.current);
    setShowFullCell(true);
  };

  const handleMouseLeave = () => {
    setShowFullCell(false);
  };

  useEffect(() => {
    if (!showFullCell) {
      return undefined;
    }

    function handleKeyDown(nativeEvent: KeyboardEvent) {
      if (nativeEvent.key === 'Escape' || nativeEvent.key === 'Esc') {
        setShowFullCell(false);
      }
    }

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [setShowFullCell, showFullCell]);

  return (
    <Box
      ref={wrapper}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{
        alignItems: 'center',
        lineHeight: '24px',
        width: width || '200px',
        height: '100%',
        position: 'relative',
        display: 'flex',
        ...cellStyle,
      }}
      {...rest}
    >
      <Box
        ref={cellDiv}
        sx={{
          height: '100%',
          width: width || '200px',
          display: 'block',
          position: 'absolute',
          top: 0,
        }}
      />
      <Stack direction="row" spacing={1} width={'200px'}>
        <Tooltip title={value}>
          <Typography
            ref={cellValue}
            sx={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {value}
          </Typography>
        </Tooltip>
        {icon}
      </Stack>
      {showPopper && anchorEl && wrapper.current && (
        <Popper
          open={showFullCell}
          anchorEl={anchorEl}
          sx={{ width, marginLeft: -17 }}
        >
          <Paper
            elevation={1}
            sx={{
              minHeight: Math.max((wrapper.current?.offsetHeight ?? 0) - 3, 0),
              width: 'fit-content',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                padding: 1,
                whiteSpace: 'wrap',
                wordBreak: 'break-word',
              }}
            >
              {value}
            </Typography>
          </Paper>
        </Popper>
      )}
    </Box>
  );
});
